// Masthead nav — order and visibility rules are law, not layout choices.
//
// Order: My Desk, Tasks, People, Company, Payroll, Recruiting, LINKS.
// Money and Reports are tabs inside Company now, not top-line items (owner,
// 2026-10-06: "Id like to move reports and Money under company as a sub menu
// item"); their old addresses still work (lib/router.ts legacyRedirect).
// Recruiting sits between Payroll and LINKS (owner, 2026-09-25: "move
// recruiting out of People as its own top line between payroll and LINKS").
// The Drive / Gmail marks stay on the far right.

export type Role = 'OWNER' | 'DIRECTOR' | 'MANAGER' | 'FRONT_DESK' | 'ASSISTANT';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  muted?: boolean;
  /** The opening letters of a product name that stay lowercase inside the
   * menu's capitals — nvRecruiter shows as nvRECRUITER (owner, 2026-09-26:
   * "make the nv lower case"). Everything else about the item is like its
   * neighbours. */
  lowerPrefix?: string;
}

// The single canonical order. Nothing else in this app may reorder these.
// First item is My Desk (owner, 2026-08-19) — same path `/`, new name.
// The Drive logo on the far right is a different thing (D-36).
const PRIMARY_NAV: NavItem[] = [
  { key: 'home', label: 'My Desk', path: '/' },
  { key: 'tasks', label: 'Tasks', path: '/tasks' },
  { key: 'people', label: 'People', path: '/people' },
  { key: 'company', label: 'Company', path: '/company' },
  { key: 'payroll', label: 'Payroll', path: '/payroll' },
  { key: 'recruiting', label: 'nvRecruiter', path: '/recruiting', lowerPrefix: 'nv' },
  { key: 'links', label: 'LINKS', path: '/links' },
  // The Guide is a tab inside nvRecruiter, not a top-menu item (owner,
  // 2026-10-06: "guide menu item is supposed to be under nvRecruiter").
];

export const MY_DESK_ITEM: NavItem = { key: 'my-desk', label: 'My Desk', path: '/my-desk' };

/**
 * What the far-right nav item is CALLED for this person (OWNER RULING D-36,
 * 2026-08-15, verbatim: "its MY DRIVE for all manager and opens their Manager
 * Drive on Google Drive. Candace's wil be calle C DRIVE. mine will be T
 * DRIVE").
 *
 * It is a link to a Google Drive folder, not a page in this app — see
 * `GET /api/v1/my-drive`, which resolves the folder from the session.
 */
export function driveLabelFor(role: Role): string {
  if (role === 'OWNER') return 'T Drive';
  if (role === 'DIRECTOR') return 'C Drive';
  return 'My Drive';
}

/**
 * Nav items a given role should see, in canonical order, EXCLUDING My Desk
 * (which always renders far right with a separator — see Nav.tsx).
 */
export function primaryNavFor(role: Role): NavItem[] {
  // Payroll is visible to EVERY mapped role as of 2026-08-15 (D-40), and the
  // rule it used to hide from a manager is unchanged: the payroll ARCHIVE is
  // still admin-only, still refused server-side by
  // `checkPayrollArchiveAccess` (R-5), and a manager who reaches it still gets
  // that refusal in the server's own words.
  //
  // What changed is that Payroll is no longer only the archive. A manager's
  // weekly entry form lives there now (src/manager_entry.js), and in the live
  // app the same is true — `docs/APPS-SCRIPT-ACCESS-MAP.md` §2: the nav link
  // is not hidden for a manager, it is ROUTED differently (admin →
  // `?page=payroll`, manager → `?page=paydash`), and Payroll Extras is
  // "full read-write on own location only". Hiding the item was this
  // rebuild being stricter than the system it replaces, and it left a
  // manager with no way to reach the one payroll thing that is theirs.
  return [...PRIMARY_NAV];
}

/**
 * Whether the Drive item renders at all.
 *
 * It renders for EVERY mapped role now. R-6/R-7 excluded Candace from the old
 * "My Desk" — a per-user private folder inside this app. The owner replaced
 * that concept on 2026-08-15 (D-36): the item is a link to a Google Drive
 * folder, and Candace has one, called C Drive. That is the owner reopening his
 * own ruling, which is the only way a ratified ruling ever changes.
 */
export function showMyDesk(role: Role): boolean {
  return role === 'OWNER' || role === 'DIRECTOR' || role === 'MANAGER';
}
